import type { TechnicalReviewStatus } from "./technical-document-review.js";
import type { IntegrityReportStatus } from "../report/integrity-report-domain.js";

/**
 * STATUS DE WORKFLOW padronizados do Painel de Engenharia. Cada módulo
 * mantém o seu enum interno; aqui fica só a tradução para os estados que a
 * interface mostra — o enum interno nunca chega à tela.
 */
export type EngineeringWorkflowStatus =
  | "EM_ELABORACAO"
  | "AGUARDANDO_ANALISE_PLH"
  | "EM_ANALISE_PLH"
  | "CORRECAO_SOLICITADA"
  | "CORRECAO_EM_ANDAMENTO"
  | "REENVIADO_AO_PLH"
  | "APROVADO_PELO_PLH"
  | "REPROVADO"
  | "MINUTA_PENDENTE"
  | "MINUTA_VALIDADA"
  | "EM_HOLD"
  | "AGUARDANDO_FINALIZACAO"
  | "FINALIZADO_AGUARDANDO_ASSINATURA"
  | "FINALIZADO"
  | "ASSINADO";

export const ENGINEERING_WORKFLOW_STATUS_LABELS: Record<EngineeringWorkflowStatus, string> = {
  EM_ELABORACAO: "Em Elaboração",
  AGUARDANDO_ANALISE_PLH: "Aguardando Análise do PLH",
  EM_ANALISE_PLH: "Em Análise do PLH",
  CORRECAO_SOLICITADA: "Correção Solicitada",
  CORRECAO_EM_ANDAMENTO: "Correção em Andamento",
  REENVIADO_AO_PLH: "Reenviado ao PLH",
  APROVADO_PELO_PLH: "Aprovado pelo PLH",
  REPROVADO: "Reprovado",
  MINUTA_PENDENTE: "Minuta Pendente",
  MINUTA_VALIDADA: "Minuta Validada",
  EM_HOLD: "Em HOLD",
  AGUARDANDO_FINALIZACAO: "Aguardando Finalização",
  FINALIZADO_AGUARDANDO_ASSINATURA: "Finalizado — Aguardando Assinatura",
  FINALIZADO: "Finalizado",
  ASSINADO: "Assinado"
};

/** AVR: elaboração → análise do PLH → aprovada/reprovada → assinada; finalização à parte. */
export function workflowStatusOfAvr(avr: { status: string; finalization?: { state: string } | null }): EngineeringWorkflowStatus {
  if (avr.finalization) return avr.finalization.state === "FINALIZADO" ? "FINALIZADO" : "FINALIZADO_AGUARDANDO_ASSINATURA";
  switch (avr.status) {
    case "AWAITING_PLH_REVIEW": return "AGUARDANDO_ANALISE_PLH";
    case "PLH_REVIEWING": return "EM_ANALISE_PLH";
    case "CORRECTION_REQUESTED": return "CORRECAO_SOLICITADA";
    case "PLH_APPROVED": return "APROVADO_PELO_PLH";
    case "REJECTED": return "REPROVADO";
    case "SIGNED": return "ASSINADO";
    default: return "EM_ELABORACAO";
  }
}

/** Estados que contam como "pendência" para a Engenharia/PLH. */
export const ENGINEERING_PENDING_STATUSES: readonly EngineeringWorkflowStatus[] = ["AGUARDANDO_ANALISE_PLH", "EM_ANALISE_PLH", "REENVIADO_AO_PLH"];

/**
 * Ciclo central (LP/ME/PM/US…): o mesmo status "aguardando" vira REENVIADO
 * quando o ciclo é > 1 (voltou de uma correção); "correção em andamento"
 * quando o elaborador já salvou depois da devolução.
 */
export function workflowStatusOfReview(review: { status: TechnicalReviewStatus; cycle: number; correctedAt: string | null; signedAt: string | null; finalizedAt?: string | null }): EngineeringWorkflowStatus {
  if (review.finalizedAt) return review.signedAt ? "FINALIZADO" : "FINALIZADO_AGUARDANDO_ASSINATURA";
  if (review.signedAt) return "ASSINADO";
  switch (review.status) {
    case "AWAITING_PLH_REVIEW": return review.cycle > 1 ? "REENVIADO_AO_PLH" : "AGUARDANDO_ANALISE_PLH";
    case "PLH_REVIEWING": return "EM_ANALISE_PLH";
    case "CORRECTION_REQUESTED": return review.correctedAt ? "CORRECAO_EM_ANDAMENTO" : "CORRECAO_SOLICITADA";
    case "PLH_APPROVED": return "APROVADO_PELO_PLH";
  }
}

/**
 * Relatório de Integridade (workflow próprio) considerando a Minuta e a
 * finalização: validado sem Minuta validada → MINUTA PENDENTE; validado com
 * Minuta validada e sem finalização → AGUARDANDO FINALIZAÇÃO; finalizado →
 * FINALIZADO / FINALIZADO — AGUARDANDO ASSINATURA.
 */
export function workflowStatusOfIntegrityReportFull(report: { status: string; finalization?: { state: string } | null }, minutaStatus: string | null | undefined): EngineeringWorkflowStatus | null {
  if (report.finalization) return report.finalization.state === "FINALIZADO" ? "FINALIZADO" : "FINALIZADO_AGUARDANDO_ASSINATURA";
  if (report.status === "APPROVED" || report.status === "AWAITING_SIGNATURE") {
    if (!minutaStatus || !["CONFORME", "ANÁLISE CONCLUÍDA", "APROVADA"].includes(minutaStatus)) return "MINUTA_PENDENTE";
    return "AGUARDANDO_FINALIZACAO";
  }
  return workflowStatusOfIntegrityReport(report.status);
}

/** Relatório de Integridade (workflow próprio). */
export function workflowStatusOfIntegrityReport(status: IntegrityReportStatus | string): EngineeringWorkflowStatus | null {
  switch (status) {
    case "DRAFT":
    case "IN_ELABORATION":
    case "FINALIZED_BY_INSPECTOR": return "EM_ELABORACAO";
    case "SUBMITTED_TO_ENGINEERING": return "AGUARDANDO_ANALISE_PLH";
    case "RESUBMITTED": return "REENVIADO_AO_PLH";
    case "UNDER_ENGINEERING_REVIEW": return "EM_ANALISE_PLH";
    case "RETURNED": return "CORRECAO_SOLICITADA";
    case "IN_CORRECTION": return "CORRECAO_EM_ANDAMENTO";
    case "APPROVED":
    case "AWAITING_SIGNATURE": return "APROVADO_PELO_PLH";
    case "SIGNED": return "ASSINADO";
    case "COMPLETED":
    case "ISSUED": return "FINALIZADO";
    default: return null;
  }
}

/** Minuta de Recomendações. */
export function workflowStatusOfMinuta(status: string): EngineeringWorkflowStatus {
  switch (status) {
    case "EMITIDA":
    case "EM CORREÇÃO":
    case "ATUALIZAÇÃO PENDENTE": return "EM_ELABORACAO";
    case "AGUARDANDO ANÁLISE": return "AGUARDANDO_ANALISE_PLH";
    case "EM ANÁLISE": return "EM_ANALISE_PLH";
    case "CORREÇÃO NECESSÁRIA":
    case "AGUARDANDO INSPETOR": return "CORRECAO_SOLICITADA";
    case "AGUARDANDO VALIDAÇÃO": return "CORRECAO_EM_ANDAMENTO";
    case "ANÁLISE CONCLUÍDA":
    case "CONFORME": return "MINUTA_VALIDADA";
    case "APROVADA": return "ASSINADO";
    default: return "EM_ELABORACAO";
  }
}

export function workflowStatusLabel(status: EngineeringWorkflowStatus | null | undefined): string {
  return status ? ENGINEERING_WORKFLOW_STATUS_LABELS[status] : "—";
}
